(() => {
  const theme = window.ProjectOptimal;
  const drawer = document.getElementById("CartDrawer");
  const drawerItems = drawer?.querySelector("[data-cart-items]");
  const drawerTotal = drawer?.querySelector("[data-cart-total]");
  const cartToggles = document.querySelectorAll("[data-cart-toggle]");
  const searchPanel = document.querySelector("[data-site-search]");
  const searchToggles = document.querySelectorAll("[data-search-toggle]");
  const siteHeader = document.querySelector(".site-header");
  const mobileToggle = document.querySelector("[data-mobile-menu-toggle]");
  const mobileMenu = document.querySelector("[data-mobile-menu]");
  const mobileMenuLabel = mobileToggle?.querySelector("[data-mobile-menu-label]");
  let drawerOpener;
  let searchOpener;
  let mobileMenuOpener;
  let cartBusy = false;
  let cartRevision = 0;
  // A blurred header creates a containing block for fixed-position descendants.
  if (searchPanel) document.body.append(searchPanel);
  if (mobileMenu) document.body.append(mobileMenu);

  const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || "en", {
    style: "currency",
    currency: theme?.currency || "EUR"
  }).format((cents || 0) / 100);

  const setCartCount = (count) => {
    document.querySelectorAll('[data-free-shipping-notice]').forEach((element) => {
      element.textContent = count >= 2 ? element.dataset.unlocked : count === 1 ? element.dataset.one : element.dataset.empty;
    });
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = count;
      element.classList.toggle("is-hidden", count === 0);
    });
  };

  const syncScrollLock = () => {
    const drawerOpen = drawer?.classList.contains("open");
    const searchOpen = Boolean(searchPanel && !searchPanel.hidden);
    const menuOpen = mobileToggle?.getAttribute("aria-expanded") === "true";
    document.documentElement.classList.toggle("is-scroll-locked", Boolean(drawerOpen || searchOpen || menuOpen));
    document.querySelectorAll("#MainContent, .site-footer").forEach((element) => {
      element.inert = Boolean(drawerOpen || searchOpen || menuOpen);
    });
    if (siteHeader) siteHeader.inert = Boolean(drawerOpen || searchOpen);
  };

  const updateMobileMenuOffset = () => {
    if (!mobileMenu || !siteHeader) return;
    mobileMenu.style.setProperty("--mobile-menu-top", `${Math.max(0, siteHeader.getBoundingClientRect().bottom)}px`);
  };

  const setMobileMenuLabel = (isOpen) => {
    const label = isOpen ? "Close menu" : "Open menu";
    mobileToggle?.setAttribute("aria-label", label);
    if (mobileMenuLabel) mobileMenuLabel.textContent = label;
  };

  const closeMobileMenu = (restoreFocus = true) => {
    if (!mobileToggle || !mobileMenu) return;
    const wasOpen = mobileToggle.getAttribute("aria-expanded") === "true";
    mobileToggle.setAttribute("aria-expanded", "false");
    mobileToggle.classList.remove("is-active");
    mobileMenu.classList.remove("is-open");
    mobileMenu.setAttribute("aria-hidden", "true");
    mobileMenu.hidden = true;
    setMobileMenuLabel(false);
    syncScrollLock();
    if (wasOpen && restoreFocus && mobileMenuOpener instanceof HTMLElement && document.contains(mobileMenuOpener)) {
      mobileMenuOpener.focus();
    }
  };

  const openMobileMenu = () => {
    if (!mobileToggle || !mobileMenu) return;
    mobileMenuOpener = mobileToggle;
    updateMobileMenuOffset();
    mobileMenu.hidden = false;
    mobileMenu.classList.add("is-open");
    mobileMenu.setAttribute("aria-hidden", "false");
    mobileToggle.setAttribute("aria-expanded", "true");
    mobileToggle.classList.add("is-active");
    setMobileMenuLabel(true);
    syncScrollLock();
    mobileMenu.querySelector("a[href], summary")?.focus();
  };

  const openDrawer = () => {
    if (!drawer) return;
    closeMobileMenu(false);
    closeSearch(false);
    drawerOpener = document.activeElement;
    drawer.inert = false;
    drawer.classList.add("open");
    drawer.setAttribute("aria-hidden", "false");
    cartToggles.forEach((toggle) => toggle.setAttribute("aria-expanded", "true"));
    syncScrollLock();
    requestAnimationFrame(() => {
      if (drawer.classList.contains("open")) drawer.querySelector("button[data-cart-close]")?.focus();
    });
  };

  const closeDrawer = (restoreFocus = true) => {
    if (!drawer) return;
    const wasOpen = drawer.classList.contains("open");
    drawer.classList.remove("open");
    drawer.setAttribute("aria-hidden", "true");
    drawer.inert = true;
    cartToggles.forEach((toggle) => toggle.setAttribute("aria-expanded", "false"));
    syncScrollLock();
    if (wasOpen && restoreFocus && drawerOpener instanceof HTMLElement && document.contains(drawerOpener)) {
      drawerOpener.focus();
    }
  };

  const openSearch = () => {
    if (!searchPanel) return;
    closeMobileMenu(false);
    closeDrawer(false);
    searchOpener = document.activeElement;
    searchPanel.hidden = false;
    searchPanel.setAttribute("aria-hidden", "false");
    searchToggles.forEach((toggle) => toggle.setAttribute("aria-expanded", "true"));
    syncScrollLock();
    searchPanel.querySelector("[data-search-input]")?.focus();
  };

  const closeSearch = (restoreFocus = true) => {
    if (!searchPanel) return;
    const wasOpen = !searchPanel.hidden;
    searchPanel.hidden = true;
    searchPanel.setAttribute("aria-hidden", "true");
    searchToggles.forEach((toggle) => toggle.setAttribute("aria-expanded", "false"));
    syncScrollLock();
    if (wasOpen && restoreFocus && searchOpener instanceof HTMLElement && document.contains(searchOpener)) {
      searchOpener.focus();
    }
  };

  const cartLine = (item) => {
    const itemUrl = window.OptimalRequests.safeUrl(item.url) || theme.routes.cart;
    const imageUrl = window.OptimalRequests.safeUrl(item.image, true);
    const image = imageUrl
      ? `<a href="${escapeHtml(itemUrl)}"><img src="${escapeHtml(imageUrl)}${imageUrl.includes('?') ? '&' : '?'}width=180" width="90" height="90" alt="${escapeHtml(item.product_title)}"></a>`
      : '<div class="cart-item__placeholder" aria-hidden="true"></div>';
    const variant = item.variant_title && item.variant_title !== "Default Title"
      ? `<p>${escapeHtml(item.variant_title)}</p>`
      : "";

    const discounts = (item.line_level_discount_allocations || []).map((discount) =>
      `<span class="cart-discount">${escapeHtml(discount.discount_application.title)} (−${money(discount.amount)})</span>`
    ).join("");
    const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith("_") && value).map(([key, value]) => `<p>${escapeHtml(key)}: ${escapeHtml(value)}</p>`).join("");
    return `<article class="cart-item" data-cart-item data-cart-key="${escapeHtml(item.key)}">
      ${image}
      <div class="cart-item__details">
        <a href="${escapeHtml(itemUrl)}"><h4>${escapeHtml(item.product_title)}</h4></a>
        ${variant}
        ${properties}
        ${item.selling_plan_allocation ? `<p>${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : ""}
        <p>${money(item.final_line_price)}</p>
        ${discounts}
        <div class="cart-item__controls">
          <button type="button" data-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="Decrease quantity of ${escapeHtml(item.product_title)}">−</button>
          <span aria-label="Quantity">${item.quantity}</span>
          <button type="button" data-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity of ${escapeHtml(item.product_title)}">+</button>
          <button class="cart-item__remove" type="button" data-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="0">Remove</button>
        </div>
      </div>
    </article>`;
  };

  const renderCart = (cart) => {
    window.OptimalRequests.validateCart(cart);
    if (!drawerItems || !drawerTotal) return;
    if (cart.currency) theme.currency = cart.currency;
    const focused = document.activeElement;
    const focusedKey = focused?.dataset?.lineKey;
    const focusedQuantity = focused?.dataset?.quantity;
    drawerItems.innerHTML = cart.item_count
      ? cart.items.map(cartLine).join("")
      : `<div class="cart-empty"><p>Your cart is empty.</p><a href="${escapeHtml(theme.routes.allProducts)}" class="btn btn-outline">Shop collection</a></div>`;
    drawerTotal.textContent = money(cart.total_price);
    setCartCount(cart.item_count);
    const checkout = drawer.querySelector("[data-cart-checkout]");
    if (checkout) checkout.hidden = cart.item_count === 0;
    const discounts = drawer.querySelector("[data-cart-discounts]");
    if (discounts) discounts.innerHTML = (cart.cart_level_discount_applications || []).map((discount) => `<p class="cart-discount">${escapeHtml(discount.title)} <span>−${money(discount.total_allocated_amount)}</span></p>`).join("");
    if (focusedKey) {
      const controls = [...drawerItems.querySelectorAll("[data-cart-change]")];
      const replacement = controls.find((button) => button.dataset.lineKey === focusedKey && button.dataset.quantity === focusedQuantity)
        || controls.find((button) => button.dataset.lineKey === focusedKey)
        || drawer.querySelector("button[data-cart-close]");
      replacement?.focus();
    }
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
  };

  const getCart = async () => {
    return window.OptimalRequests.json(theme.routes.cartJson, {
      headers: { Accept: "application/json" }, cache: "no-store"
    }, { operation: 'cart' });
  };

  const refreshCart = async () => {
    const revision = cartRevision;
    const cart = await getCart();
    if (revision === cartRevision) renderCart(cart);
    return cart;
  };

  cartToggles.forEach((toggle) => {
    toggle.addEventListener("click", async (event) => {
      if (!drawer || !theme?.routes?.cartJson) return;
      event.preventDefault();
      if (toggle.getAttribute('aria-busy') === 'true') return;
      toggle.setAttribute('aria-busy', 'true');
      try {
        await refreshCart();
        openDrawer();
      } catch (_) {
        window.location.href = toggle.href;
      } finally {
        toggle.removeAttribute('aria-busy');
      }
    });
  });

  searchToggles.forEach((toggle) => {
    toggle.addEventListener("click", (event) => {
      if (!searchPanel) return;
      event.preventDefault();
      openSearch();
    });
  });

  searchPanel?.querySelectorAll("[data-search-close]").forEach((button) => {
    button.addEventListener("click", closeSearch);
  });

  drawer?.querySelectorAll("[data-cart-close]").forEach((button) => {
    button.addEventListener("click", closeDrawer);
  });

  const focusableWithin = (container) => [...container.querySelectorAll('a[href], summary, button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && searchPanel && !searchPanel.hidden) {
      closeSearch();
      return;
    }

    if (event.key === "Tab" && searchPanel && !searchPanel.hidden) {
      const focusable = focusableWithin(searchPanel);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    if (event.key === "Escape" && drawer?.classList.contains("open")) {
      closeDrawer();
      return;
    }

    if (event.key === "Escape" && mobileToggle?.getAttribute("aria-expanded") === "true") {
      closeMobileMenu();
      return;
    }

    if (event.key === "Tab" && drawer?.classList.contains("open")) {
      const focusable = focusableWithin(drawer);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    if (event.key === "Tab" && mobileToggle?.getAttribute("aria-expanded") === "true" && mobileMenu) {
      const focusable = [mobileToggle, ...focusableWithin(mobileMenu)];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  drawer?.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-cart-change]");
    if (!button || !theme?.routes || cartBusy) return;
    cartBusy = true;
    cartRevision += 1;
    const cartStatus = drawer.querySelector("[data-cart-status]");
    if (cartStatus) cartStatus.textContent = "Updating your cart…";
    drawerItems.setAttribute("aria-busy", "true");
    drawerItems.querySelectorAll("[data-cart-change]").forEach((control) => control.setAttribute("aria-disabled", "true"));
    try {
      const result = await window.OptimalRequests.json(theme.routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: button.dataset.lineKey, quantity: Number(button.dataset.quantity) })
      }, { operation: 'cart' });
      renderCart(result);
      if (cartStatus) cartStatus.textContent = "Cart updated.";
    } catch (error) {
      if (error.uncertain) { window.location.href = theme.routes.cart; return; }
      if (cartStatus) cartStatus.textContent = error.message;
    } finally {
      cartBusy = false;
      drawerItems.removeAttribute("aria-busy");
      drawerItems.querySelectorAll("[data-cart-change]").forEach((control) => control.removeAttribute("aria-disabled"));
    }
  });

  const initializeProducts = (root = document) => {
  root.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
    if (gallery.dataset.productGalleryReady) return;
    gallery.dataset.productGalleryReady = "true";
    const controller = new AbortController();
    const slides = gallery.querySelectorAll("[data-gallery-slide]");
    const thumbnails = gallery.querySelectorAll("[data-gallery-thumbnail]");

    const showImage = (imageId) => {
      if (![...slides].some(slide => slide.dataset.imageId === String(imageId) && slide.dataset.colorExcluded !== 'true')) return;
      imageId = String(imageId);
      slides.forEach((slide) => {
        slide.hidden = slide.dataset.imageId !== imageId;
      });
      thumbnails.forEach((thumbnail) => {
        const isActive = thumbnail.dataset.imageId === imageId;
        thumbnail.classList.toggle("is-active", isActive);
        if (isActive) thumbnail.setAttribute("aria-current", "true");
        else thumbnail.removeAttribute("aria-current");
      });
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("click", () => showImage(thumbnail.dataset.imageId), { signal: controller.signal });
    });

    gallery.showImage = showImage;
    gallery.addEventListener("product-core:unload", () => {
      controller.abort();
      delete gallery.dataset.productGalleryReady;
      delete gallery.showImage;
    }, { once: true });
  });

  root.querySelectorAll("[data-product-form]").forEach((form) => {
    if (form.dataset.productFormReady) return;
    const formId = form.getAttribute("id");
    const variantsElement = document.getElementById(`ProductVariants-${formId.replace("ProductForm-", "")}`);
    let variants;
    try { variants = variantsElement ? JSON.parse(variantsElement.textContent) : []; }
    catch (_) { return; }
    const optionSelects = form.querySelectorAll("[data-option-select]");
    const variantIdInput = form.querySelector("[data-variant-id]");
    const price = form.closest(".product-details")?.querySelector("[data-product-price-current]");
    const comparePrice = form.closest(".product-details")?.querySelector("[data-product-price-compare]");
    const status = form.querySelector("[data-product-status]");
    const submit = form.querySelector("[data-add-to-cart]");
    const submitLabel = form.querySelector("[data-add-to-cart-label]");
    const stickyBar = document.querySelector('[data-product-sticky-for="' + formId + '"]');
    const stickySubmit = stickyBar?.querySelector("[data-product-sticky-submit]");
    const stickyLabel = stickyBar?.querySelector("[data-product-sticky-label]");
    const stickyPrice = stickyBar?.querySelector("[data-product-sticky-price]");
    const stickyVariant = stickyBar?.querySelector("[data-product-sticky-variant]");
    const stickyStatus = stickyBar?.querySelector("[data-product-sticky-status]");
    const stickyEdit = stickyBar?.querySelector("[data-product-sticky-edit]");
    const quantityInput = form.querySelector('[name="quantity"]');
    if (!submit || !submitLabel || !variantIdInput) return;
    form.dataset.productFormReady = "true";
    const controller = new AbortController();
    const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: controller.signal });
    let stickyAnimationFrame;
    let stickyInitiated = false;
    let adding = false;
    let selectedVariant = variants.find((variant) => String(variant.id) === variantIdInput.value);
    const acceleratedPayment = form.querySelector("[data-product-payment], .shopify-payment-button");
    if (variants.length) {
      const fallback = form.querySelector("[data-variant-fallback]");
      const options = form.querySelector("[data-product-options]");
      if (fallback) fallback.hidden = true;
      if (options) options.hidden = false;
      variantIdInput.required = false;
    }

    const syncStickyPurchase = (variant = selectedVariant) => {
      if (!stickyBar || !stickySubmit || !stickyLabel || !stickyPrice) return;
      const isAvailable = variant ? variant.available : !submit.disabled;
      stickyPrice.textContent = variant ? money(variant.price) : (price?.textContent || "");
      stickySubmit.disabled = adding || !isAvailable;
      stickyLabel.textContent = adding ? "Adding…" : (isAvailable ? "Add to cart" : "Unavailable");
      if (stickyVariant) {
        const label = optionSelects.length ? [...optionSelects].map(select => select.value).join(" / ") : "";
        stickyVariant.textContent = `${label}${label ? " · " : ""}Qty ${quantityInput?.value || 1}`;
        stickyVariant.title = stickyVariant.textContent;
      }
    };

    const updateVariant = (updateUrl = false, preserveStatus = false) => {
      if (!variants.length || !optionSelects.length) {
        if (acceleratedPayment) acceleratedPayment.hidden = !selectedVariant?.available;
        syncStickyPurchase();
        return;
      }
      const values = [...optionSelects].map((select) => select.value);
      const variant = variants.find((candidate) => candidate.options.every((option, index) => option === values[index]));
      const gallery = form.closest(".product-page")?.querySelector("[data-product-gallery]");
      const colorIndex = [...optionSelects].findIndex(select => /^(color|colour|farbe|couleur|colore|coloris)$/i.test((select.dataset.optionName || '').trim()));
      const imageIdFor = candidate => candidate?.featured_image?.id || candidate?.featured_media?.preview_image?.id;
      const hasGalleryImage = candidate => {
        const id = imageIdFor(candidate);
        return id && [...(gallery?.querySelectorAll('[data-gallery-slide]') || [])].some(slide => slide.dataset.imageId === String(id));
      };
      const imageVariant = hasGalleryImage(variant) ? variant : colorIndex >= 0
        ? variants.find(candidate => candidate.options[colorIndex] === values[colorIndex] && hasGalleryImage(candidate)) : null;
      const imageId = imageIdFor(imageVariant) || gallery?.dataset.defaultImageId;
      if (gallery) {
        const normalize = value => String(value || '').trim().toLocaleLowerCase();
        const selectedColor = normalize(values[colorIndex]);
        const sameColorIds = new Set(variants.filter(candidate => colorIndex >= 0 && normalize(candidate.options[colorIndex]) === selectedColor).map(imageIdFor).filter(Boolean).map(String));
        const gallerySlides = [...gallery.querySelectorAll('[data-gallery-slide]')];
        const matchesColor = slide => {
          if (colorIndex < 0) return true;
          const tags = [...(slide.dataset.imageDescription || '').matchAll(/\[(?:color|colour|farbe):([^\]]+)\]/gi)].map(match => normalize(match[1]));
          return tags.length ? tags.includes(selectedColor) : sameColorIds.has(slide.dataset.imageId);
        };
        // Unassigned photos are not assumed to show the chosen color.
        const matching = gallerySlides.filter(matchesColor);
        const allowedIds = new Set(matching.length ? matching.map(slide => slide.dataset.imageId) : [String(imageId)]);
        gallerySlides.forEach(slide => {
          slide.dataset.colorExcluded = String(!allowedIds.has(slide.dataset.imageId));
          if (slide.dataset.colorExcluded === 'true') slide.hidden = true;
        });
        gallery.querySelectorAll('[data-gallery-thumbnail]').forEach(thumbnail => { thumbnail.hidden = !allowedIds.has(thumbnail.dataset.imageId); });
        const targetId = allowedIds.has(String(imageId)) ? imageId : matching[0]?.dataset.imageId;
        if (targetId && typeof gallery.showImage === 'function') gallery.showImage(String(targetId));
        gallery.dispatchEvent(new Event('gallery:color-change'));
      }
      selectedVariant = variant;
      if (acceleratedPayment) acceleratedPayment.hidden = !variant?.available;

      if (!variant) {
        variantIdInput.value = "";
        submit.disabled = true;
        submitLabel.textContent = "Unavailable";
        status.textContent = "This combination is unavailable.";
        syncStickyPurchase();
        return;
      }

      variantIdInput.value = variant.id;
      price.textContent = money(variant.price);
      comparePrice.textContent = variant.compare_at_price ? money(variant.compare_at_price) : "";
      comparePrice.classList.toggle("is-hidden", !(variant.compare_at_price > variant.price));
      submit.disabled = adding || !variant.available;
      submitLabel.textContent = adding ? "Adding…" : (variant.available ? "Add to cart" : "Sold out");
      if (!preserveStatus) status.textContent = variant.available ? "Available" : "This variant is currently sold out.";
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }

      syncStickyPurchase(variant);
    };

    optionSelects.forEach((select) => listen(select, "change", () => {
      if (stickyStatus) { stickyStatus.textContent = ""; stickyStatus.hidden = true; }
      updateVariant(true);
    }));
    listen(quantityInput, "input", () => syncStickyPurchase());
    listen(stickyEdit, "click", (event) => {
      event.preventDefault();
      const choice = form.querySelector('[data-option-value][aria-pressed="true"]:not([disabled])');
      const nativeChoice = [...optionSelects].find(select => !select.hidden && !select.disabled);
      const target = choice || nativeChoice || quantityInput || submit;
      target.focus({ preventScroll: true });
      form.scrollIntoView({ block: "start", behavior: "auto" });
    });
    updateVariant();

    if (stickyBar && stickySubmit) {
      const updateStickyVisibility = () => {
        stickyAnimationFrame = undefined;
        stickyBar.hidden = !(submit.getBoundingClientRect().bottom < 0);
      };

      listen(window, "scroll", () => {
        if (!stickyAnimationFrame) {
          stickyAnimationFrame = window.requestAnimationFrame(updateStickyVisibility);
        }
      }, { passive: true });
      listen(window, "resize", updateStickyVisibility);
      updateStickyVisibility();

      listen(stickySubmit, "click", () => {
        if (!stickySubmit.disabled) {
          if (!form.reportValidity()) {
            form.querySelector(":invalid")?.scrollIntoView({ block: "center" });
            return;
          }
          stickyInitiated = true;
          form.requestSubmit(submit);
        }
      });
    }

    listen(form, "submit", async (event) => {
      if (!theme?.routes?.cartAdd) return;
      if (event.submitter && event.submitter !== submit) return;
      event.preventDefault();
      const fromSticky = stickyInitiated;
      stickyInitiated = false;
      if (adding || cartBusy || submit.disabled || !variantIdInput.value) return;
      adding = true;
      cartBusy = true;
      cartRevision += 1;
      const formData = new FormData(form);
      if (stickyStatus) { stickyStatus.textContent = ""; stickyStatus.hidden = true; }
      submit.disabled = true;
      const originalLabel = submitLabel.textContent;
      submitLabel.textContent = "Adding…";
      status.textContent = "";
      optionSelects.forEach((select) => { select.disabled = true; });
      if (quantityInput) quantityInput.disabled = true;
      if (acceleratedPayment) acceleratedPayment.inert = true;
      syncStickyPurchase();

      try {
        await window.OptimalRequests.json(theme.routes.cartAdd, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData
        }, { operation: 'cart' });
        status.textContent = "Added to cart.";
        try {
          await refreshCart();
          if (drawer) openDrawer();
          else window.location.href = theme.routes.cart;
        } catch (_) {
          // The POST succeeded: send the buyer to their cart instead of inviting a duplicate add.
          window.location.href = theme.routes.cart;
        }
      } catch (error) {
        if (error.uncertain) { window.location.href = theme.routes.cart; return; }
        status.textContent = error.message || "This item could not be added. Please try again.";
        if (fromSticky && stickyStatus) {
          stickyStatus.textContent = status.textContent;
          stickyStatus.hidden = false;
        }
      } finally {
        adding = false;
        cartBusy = false;
        submit.disabled = selectedVariant ? !selectedVariant.available : false;
        submitLabel.textContent = originalLabel;
        optionSelects.forEach((select) => { select.disabled = false; });
        if (quantityInput) quantityInput.disabled = false;
        if (acceleratedPayment) acceleratedPayment.inert = false;
        updateVariant(false, true);
      }
    });
    form.addEventListener("product-core:unload", () => {
      controller.abort();
      if (stickyAnimationFrame) window.cancelAnimationFrame(stickyAnimationFrame);
      delete form.dataset.productFormReady;
    }, { once: true });
  });
  };
  initializeProducts();
  document.addEventListener("shopify:section:load", (event) => initializeProducts(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-product-form], [data-product-gallery]").forEach(element => element.dispatchEvent(new Event("product-core:unload")));
  });

  document.querySelectorAll("[data-quick-add]").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      if (!theme?.routes?.cartAdd) return;
      event.preventDefault();
      if (cartBusy) return;
      cartBusy = true;
      cartRevision += 1;

      const button = form.querySelector("button[type='submit']");
      const label = form.querySelector("[data-quick-add-label]");
      const status = form.querySelector("[data-quick-add-status]");
      const originalLabel = label.textContent;
      button.disabled = true;
      label.textContent = "Adding…";

      try {
        await window.OptimalRequests.json(theme.routes.cartAdd, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form)
        }, { operation: 'cart' });
        status.textContent = "Added to cart.";
        try {
          await refreshCart();
          if (drawer) openDrawer();
          else window.location.href = theme.routes.cart;
        } catch (_) { window.location.href = theme.routes.cart; }
      } catch (error) {
        if (error.uncertain) { window.location.href = theme.routes.cart; return; }
        status.textContent = error.message || "This item could not be added.";
      } finally {
        cartBusy = false;
        button.disabled = false;
        label.textContent = originalLabel;
      }
    });
  });

  document.querySelectorAll("[data-collection-controls]").forEach((controls) => {
    const toggle = controls.querySelector("[data-collection-filter-toggle]");
    const filters = controls.querySelector("[data-collection-filters]");
    const sort = controls.querySelector("[data-collection-sort]");

    toggle?.addEventListener("click", () => {
      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!isOpen));
      filters.hidden = isOpen;
    });

    sort?.addEventListener("change", () => controls.requestSubmit());
  });

  mobileToggle?.addEventListener("click", () => {
    const isOpen = mobileToggle.getAttribute("aria-expanded") === "true";
    if (isOpen) closeMobileMenu();
    else openMobileMenu();
  });

  mobileMenu?.addEventListener("click", (event) => {
    if (event.target.closest("a[href]")) closeMobileMenu(false);
  });

  window.addEventListener("resize", () => {
    if (window.matchMedia("(min-width: 901px)").matches) closeMobileMenu(false);
    else if (mobileToggle?.getAttribute("aria-expanded") === "true") updateMobileMenuOffset();
  });
})();
